import { useState, useRef, useCallback, useEffect } from "react";
import { Popover, PopoverBody } from "reactstrap";
import classnames from "classnames";

import EmojiPickerPopover from "./EmojiPickerPopover";
import useClickAway from "../../hooks/useClickAway";

import { ReactComponent as EmojiIcon } from "@assets/ic-emoji-picker.svg";

import styles from "./index.module.scss";

const EmojiPicker = ({
  style = {},
  emojiClassName = "",
  disabled,
  value,
  setValue,
  inputRef,
  setParentHeight,
  placement = "top-start",
  popperClassName = "",
  offset = [],
}) => {
  const [data, setData] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const pickerRef = useRef(null);

  const onToggle = () => setIsOpen((prev) => !prev);

  useClickAway(popoverRef, onToggle, "mousedown");

  useEffect(() => {
    const getData = async () => {
      const json = await import("@emoji-mart/data/sets/14/apple.json");
      setData(json);
    };

    void getData();
  }, []);

  const onEmojiSelect = useCallback(
    (emoji) => {
      const stringValue = value || "";

      const currentSelectionStart = inputRef
        ? inputRef?.current.selectionStart
        : stringValue.length - 4;
      const currentSelectionEnd = inputRef
        ? inputRef?.current.selectionEnd
        : stringValue.length - 4;
      let replacedText = "".concat(
        stringValue.substring(0, currentSelectionStart),
        emoji,
        stringValue.substring(currentSelectionEnd, stringValue.length),
      );
      if (replacedText.length >= 2000) {
        replacedText = replacedText.slice(0, 2000);
      }
      if (replacedText.length < 2000) {
        setValue(replacedText);

        setParentHeight?.(`${inputRef?.current?.scrollHeight}px`);

        inputRef?.current.setSelectionRange(
          currentSelectionEnd + emoji.length,
          currentSelectionEnd + emoji.length,
          "forward",
        );
      }
    },
    [value, setValue, inputRef, setParentHeight],
  );

  return (
    <div className="d-none d-xl-flex">
      <div
        ref={pickerRef}
        className={classnames(
          "d-flex align-items-end",
          disabled && "pe-none",
          styles.emojiPopoverButton,
          disabled && styles.emojiPopoverButtonDisabled,
          emojiClassName,
        )}
        style={style}
        onClick={onToggle}
      >
        <EmojiIcon emojiClassName={styles.emojiPopoverButtonIcon} />
      </div>
      <Popover
        innerClassName="d-flex"
        popperClassName={classnames(styles.emojiPopover, popperClassName)}
        placement={placement}
        target={pickerRef}
        isOpen={isOpen}
        toggle={onToggle}
        hideArrow
        offset={offset}
      >
        <div
          ref={popoverRef}
          style={{
            // We need to add this, so the popover will calculate the position correctly even if the component is not mounted yet
            minWidth: "348px",
            minHeight: "284px",
          }}
        >
          <PopoverBody className={styles.emojiPopoverBody}>
            <EmojiPickerPopover data={data} onSelect={onEmojiSelect} />
          </PopoverBody>
        </div>
      </Popover>
    </div>
  );
};

export default EmojiPicker;
